import * as React from "react"
import { cn } from "@/lib/utils"
import { Accordion } from "./accordion"

export interface AccordionItem {
  /**
   * The question text
   */
  question: string
  /**
   * The answer text
   */
  answer: string
}

export interface AccordionGroupProps extends Omit<React.HTMLAttributes<HTMLDivElement>, 'children'> {
  /**
   * Array of FAQ items to display
   */
  items: AccordionItem[]
  /**
   * Index of the initially expanded item (optional)
   */
  defaultExpandedIndex?: number
}

/**
 * AccordionGroup component for managing multiple FAQ items
 *
 * Implements single-open behavior where opening one item automatically
 * closes any previously open item.
 *
 * @example
 * ```tsx
 * const faqItems = [
 *   { question: "What is ANYW3AR?", answer: "ANYW3AR is..." },
 *   { question: "How do I pair?", answer: "To pair your item..." },
 * ]
 *
 * <AccordionGroup items={faqItems} />
 * ```
 */
export const AccordionGroup = React.forwardRef<HTMLDivElement, AccordionGroupProps>(
  ({
    className,
    items,
    defaultExpandedIndex,
    ...props
  }, ref) => {
    // Track which item is currently expanded (-1 means none)
    const [expandedIndex, setExpandedIndex] = React.useState<number>(
      defaultExpandedIndex ?? -1
    )

    const handleToggle = (index: number) => {
      // If clicking the already expanded item, close it
      // Otherwise, open the clicked item (closing any other)
      setExpandedIndex(prev => prev === index ? -1 : index)
    }

    return (
      <div
        ref={ref}
        className={cn("flex flex-col gap-[1rem]", className)}
        {...props}
      >
        {items.map((item, index) => (
          <Accordion
            key={index}
            question={item.question}
            answer={item.answer}
            expanded={expandedIndex === index}
            onToggle={() => handleToggle(index)}
          />
        ))}
      </div>
    )
  }
)

AccordionGroup.displayName = "AccordionGroup"
